import { useCallback, useRef, useState } from 'react';
import { fetchDashboardSummary } from '../api/manufacturing.js';
import {
  createShiftReport,
  fetchShiftAnalysis,
  fetchShiftReport,
  shiftScopeParams,
} from '../api/shiftReports.js';
import { useOptionalAuth } from '../auth/AuthContext.jsx';
import { PrintButton, PrintHeader } from '../components/PrintButton.jsx';
import { AiReportView } from '../components/reports/AiReportView.jsx';
import { ReportHistory } from '../components/reports/ReportHistory.jsx';
import { ShiftMetricsPanel } from '../components/reports/ShiftMetricsPanel.jsx';
import { ShiftReportForm } from '../components/reports/ShiftReportForm.jsx';
import { Card } from '../components/ui/Card.jsx';
import { ErrorCallout, LoadingState } from '../components/ui/StateViews.jsx';
import { useApiResource } from '../hooks/useApiResource.js';
import { utcOffsetMinutes } from '../lib/dates.js';
import { formatDate } from '../lib/format.js';
import { pdfFileTitle } from '../lib/print.js';

/**
 * AI shift reports (Module 3).
 *
 * view: { kind: 'idle' }
 *     | { kind: 'loading', action: 'preview' | 'generate' | 'open' }
 *     | { kind: 'analysis', metrics }
 *     | { kind: 'report', report, reused? }
 *     | { kind: 'error', error, action }
 */
export function ReportsPage() {
  const [view, setView] = useState({ kind: 'idle' });
  const [refreshToken, setRefreshToken] = useState(0);
  const controllerRef = useRef(null);

  // Shift codes and the latest data date, used only as form suggestions.
  const context = useApiResource(
    ({ signal }) => fetchDashboardSummary({ utc_offset_minutes: utcOffsetMinutes(null) }, { signal }),
    'report-context',
  );

  const run = useCallback(async (action, task) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setView({ kind: 'loading', action });
    try {
      const next = await task(controller.signal);
      if (!controller.signal.aborted) setView(next);
    } catch (error) {
      if (!controller.signal.aborted) setView({ kind: 'error', error, action });
    } finally {
      if (action === 'generate') setRefreshToken((n) => n + 1); // failed attempts are saved too
    }
  }, []);

  const preview = (scope) =>
    run('preview', async (signal) => ({
      kind: 'analysis',
      metrics: await fetchShiftAnalysis(shiftScopeParams(scope), { signal }),
    }));

  const generate = (scope, { regenerate }) =>
    run('generate', async (signal) => {
      const { report, reused } = await createShiftReport(shiftScopeParams(scope), { regenerate, signal });
      return { kind: 'report', report, reused };
    });

  const open = useCallback(
    (id) => run('open', async (signal) => ({ kind: 'report', report: await fetchShiftReport(id, { signal }) })),
    [run],
  );

  const busy = view.kind === 'loading' ? view.action : false;
  const failedReportId = view.kind === 'error' ? view.error?.details?.find((d) => d?.report_id)?.report_id : null;
  const selectedId = view.kind === 'report' ? view.report.id : null;
  const auth = useOptionalAuth();
  const printed = view.kind === 'report' ? view.report.metrics : view.kind === 'analysis' ? view.metrics : null;
  const printTitle = printed
    ? `${view.kind === 'report' ? 'AI shift report' : 'Shift metrics'}: shift ${printed.shift}, ${formatDate(printed.production_date)}`
    : null;

  return (
    <div className="reports">
      {printTitle && <PrintHeader title={printTitle} generatedBy={auth?.user?.display_name} />}
      <header className="page-header no-print">
        <div>
          <h2 className="page-title">AI shift reports</h2>
          <p className="page-subtitle">
            Metrics are calculated from recorded data. The written report is generated by an AI model from those
            metrics only.
          </p>
        </div>
        {printTitle && (
          <div className="page-header__actions">
            <PrintButton
              title={pdfFileTitle(
                view.kind === 'report' ? 'shift report' : 'shift metrics',
                printed.production_date,
                `shift ${printed.shift}`,
              )}
            />
          </div>
        )}
      </header>

      <Card
        className="no-print"
        title="Request a shift report"
        subtitle="Preview the measured metrics, or generate an AI report from them"
      >
        <ShiftReportForm
          initialDate={context.data?.data_range.last_date ?? ''}
          shiftOptions={context.data?.shifts ?? []}
          busy={busy}
          onPreview={preview}
          onGenerate={generate}
        />
      </Card>

      {view.kind === 'loading' && (
        <Card className="no-print" title={view.action === 'generate' ? 'Generating report' : 'Loading'} busy>
          <LoadingState
            label={
              view.action === 'generate'
                ? 'Generating the AI report. This usually takes 10–60 seconds…'
                : view.action === 'preview'
                  ? 'Calculating shift metrics…'
                  : 'Loading report…'
            }
          />
        </Card>
      )}

      {view.kind === 'error' && (
        <Card title="Request failed">
          <ErrorCallout error={view.error} />
          {failedReportId && (
            <button type="button" className="button button--ghost button--small" onClick={() => open(failedReportId)}>
              View the saved failed attempt (#{failedReportId})
            </button>
          )}
        </Card>
      )}

      {view.kind === 'analysis' && (
        <Card title="Measured shift metrics" subtitle="Calculated from database records — no AI involved">
          <ShiftMetricsPanel metrics={view.metrics} />
        </Card>
      )}

      {view.kind === 'report' && (
        <>
          {view.reused && (
            <p className="callout callout--info no-print" role="status">
              An existing report was returned because the shift data has not changed since it was generated. Tick
              “Generate a new report” to create a fresh one.
            </p>
          )}
          <Card title={`Shift ${view.report.shift} report`} subtitle="AI-generated narrative with its evidence">
            <AiReportView report={view.report} />
          </Card>
          <Card title="Measured shift metrics" subtitle="The snapshot the report was generated from">
            <ShiftMetricsPanel metrics={view.report.metrics} />
          </Card>
        </>
      )}

      <div className="no-print">
        <ReportHistory refreshToken={refreshToken} selectedId={selectedId} onSelect={open} />
      </div>
    </div>
  );
}